/**
 * Source: Figma "RM V2 Library (MUI v7.2.0)" — Theme page, `Variables Design Sheet`
 * → `fontFamilyText`, `fontFamilyHeader`, `fontWeight*`, `fontSize/h1`–`h6`, `lineHeight/h1`–`h6`.
 * https://www.figma.com/design/kMeScGRhmLq2l22M2ZkmiE/RM-V2-Library--MUI-v7.2.0-?node-id=11207-164308
 *
 * Two families per Figma — Inter for body text, Lexend for headers.
 * Concrete CSS family strings live here as fallbacks; theme.typography wires
 * the CSS variables that next/font produces (--rmh-font-text, --rmh-font-header).
 *
 * Verified with design (IL - 05/08/2026)
 */
export const fontFamily = {
  text: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  header:
    '"Lexend", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

export type ResponsiveTypography = {
  fontSize: string;
  lineHeight: string;
  desktop: { fontSize: string; lineHeight: string };
};

export const headingScale = {
  h1: {
    fontSize: "2rem",
    lineHeight: "2.5rem",
    desktop: { fontSize: "2.5rem", lineHeight: "3rem" },
  },
  h2: {
    fontSize: "1.75rem",
    lineHeight: "2.25rem",
    desktop: { fontSize: "2rem", lineHeight: "2.5rem" },
  },
  h3: {
    fontSize: "1.5rem",
    lineHeight: "2rem",
    desktop: { fontSize: "1.75rem", lineHeight: "2.25rem" },
  },
  h4: {
    fontSize: "1.25rem",
    lineHeight: "1.75rem",
    desktop: { fontSize: "1.5rem", lineHeight: "2rem" },
  },
  h5: {
    fontSize: "1.125rem",
    lineHeight: "1.5rem",
    desktop: { fontSize: "1.25rem", lineHeight: "1.75rem" },
  },
  h6: {
    fontSize: "1rem",
    lineHeight: "1.25rem",
    desktop: { fontSize: "1.125rem", lineHeight: "1.25rem" },
  },
} as const satisfies Record<string, ResponsiveTypography>;

/**
 * Body ramp matches Figma `typography/body/{sm,md,lg}/{light,regular,medium,semiBold,bold}`.
 * Each size is expanded across the full weight axis so consumers can pull e.g. `bodyScale.md.medium`.
 */
const bodyBase = (
  fontSize: string,
  lineHeight: string,
  letterSpacing?: string
) => {
  const base = {
    fontFamily: "var(--rmh-font-text)",
    fontSize,
    fontStyle: "normal",
    lineHeight,
    ...(letterSpacing ? { letterSpacing } : {}),
  };

  return {
    light: {
      ...base,
      fontWeight: fontWeight.light,
    },
    regular: {
      ...base,
      fontWeight: fontWeight.regular,
    },
    medium: {
      ...base,
      fontWeight: fontWeight.medium,
    },
    semiBold: {
      ...base,
      fontWeight: fontWeight.semiBold,
    },
    bold: {
      ...base,
      fontWeight: fontWeight.bold,
    },
  };
};

export const bodyScale = {
  xs: bodyBase(
    "0.75rem", // 12px
    "1.125rem", // 18px
    "0.0125rem" // 0.2px
  ),
  sm: bodyBase(
    "0.875rem", // 14px
    "1.25rem" // 20px
  ),
  md: bodyBase(
    "1rem", // 16px
    "1.5rem" // 24px
  ),
  lg: bodyBase(
    "1.125rem", // 18px
    "1.5rem" // 24px
  ),
  xl: bodyBase(
    "1.25rem", // 20px
    "2rem", // 32px
    "0.009375rem" // 0.15px
  ),
} as const;

/**
 * Button text ramp matches Figma `typography/button/{sm,md,lg}`. One canonical
 * weight per size (medium) — buttons don't expose a weight axis. Only `sm` is
 * defined for now; remaining sizes will be added when consumed.
 */
export const buttonScale = {
  sm: {
    fontFamily: "var(--rmh-font-text)",
    fontSize: "0.875rem", // 14px
    fontStyle: "normal",
    fontWeight: fontWeight.medium,
    lineHeight: "1.125rem", // 18px
    letterSpacing: "0.025rem", // 0.4px
  },
} as const;

/**
 * Input label ramp matches Figma `typography/input/label/{sm,md,lg}`. One
 * canonical weight per size (medium). Only `md` is defined for now; remaining
 * sizes will be added when consumed.
 */
export const inputLabelScale = {
  md: {
    fontFamily: "var(--rmh-font-text)",
    fontSize: "0.875rem", // 14px
    fontStyle: "normal",
    fontWeight: fontWeight.medium,
    lineHeight: "1rem", // 16px
    letterSpacing: "0.01875rem", // 0.3px
  },
} as const;
